import { Location } from "@angular/common";
import { Component, OnInit, ViewChild } from "@angular/core";
import { AngularCsv } from "angular-csv-ext";
import { finalize } from "rxjs/operators";
import { AuthenticationService } from "../../../../core/authentication/authentication.service";
import { DynamicFormComponent } from "../../../../shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { NotificationService } from "../../../../shared/components/notification/notification.service";
import { BulkUploadInterface } from "../../../../shared/types/bulk-upload/bulk-upload.type";
import { UserInfo } from "../../../../shared/types/user-info";
import { LocationService } from "../location.service";
import { LocationBulkUploadService } from "./location-bulk-upload.service";
import { data } from "./location-csv.json";

interface LocationBulkUploadPayload {
  csv: File;
}
interface CsvData {
  name: string;
  description?: string;
  address1: string;
  address2?: string;
  address3?: string;
  city: string;
  state: string;
  zip: number;
  country: string;
  phone?: string;
  primaryUser: string;
  companyIdentifier: string;
  isChannelWorks?: boolean;
}

type CsvArray = CsvData[];

@Component({
  selector: "app-location-bulk-upload",
  templateUrl: "./location-bulk-upload.component.html",
  styleUrls: ["./location-bulk-upload.component.css"],
  providers: [LocationBulkUploadService],
})
export class LocationBulkUploadComponent implements OnInit, BulkUploadInterface {
  @ViewChild(DynamicFormComponent) dynamicForm: DynamicFormComponent;

  questions: QuestionBase<string | boolean>[];
  currentUser: UserInfo;
  loading = false;

  constructor(
    private questionsService: LocationBulkUploadService,
    private locationService: LocationService,
    private notificationService: NotificationService,
    private authenticationService: AuthenticationService,
    public location: Location,
  ) {}

  ngOnInit() {
    this.questionsService.generateQuestions().subscribe((qs) => {
      this.questions = qs;
    });
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  goBack() {
    this.location.back();
  }

  onSubmit(values: LocationBulkUploadPayload) {
    this.loading = true;
    this.locationService
      .getPutSignedUrl()
      .pipe(finalize(() => this.dynamicForm.enable()))
      .subscribe(
        (r) => {
          this.locationService
            .uploadBatch(r.url, values.csv)
            .pipe(finalize(() => this.dynamicForm.enable()))
            .subscribe(
              () => {
                this.notificationService.success(
                  "New bulk upload scheduled from csv file. You will be notified by email once the file is processed.",
                );
                this.loading = false;
              },
              (_err) => {
                this.notificationService.error("Something went wrong. Try again later");
                this.loading = false;
              },
            );
        },
        (_err) => {
          this.notificationService.error("Something went wrong. Try again later");
          this.loading = false;
        },
      );
  }

  getCompanyData(): string {
    if (this.currentUser.isCompany()) {
      return this.currentUser.company.companyIdentifier;
    }
    return "C00002";
  }

  downloadCSV = (): void => {
    const headers = [
      "name",
      "description",
      "address1",
      "address2",
      "address3",
      "city",
      "state",
      "zip",
      "country",
      "phone",
      "primaryUser",
      "companyIdentifier",
    ];
    const json: CsvArray = data;
    if (this.currentUser.isGlobal()) {
      headers.push("isStaffManaged");
      json.map((object) => {
        object.isChannelWorks = true;
      });
    }
    if (this.currentUser.isCompany()) {
      json.map((object) => {
        object.companyIdentifier = this.currentUser.company.companyIdentifier;
      });
    }
    const options = {
      useHeader: false,
      headers,
      nullToEmptyString: true,
      useBom: false,
    };
    new AngularCsv(json, "location", options);
  };
}
